import { uniq } from 'lodash';
import moment from 'moment';
import React, { useMemo } from 'react';

import { TabModules, useModulesConfig } from '~/data/company';
import { PeopleTabs } from '~/data/tabs';
import { MyTeamAbsencesWidgetProps } from '~/scenes/MyWorkspace/components/Widgets/components/MyTeamAbsencesWidget/component';
import { getWidgetDataRequestRoles } from '~/scenes/MyWorkspace/components/Widgets/data';
import { People } from '~/services/Http';
import {
  useGetPublicHolidays,
  useGetTeamAbsences,
} from '~/services/Http/hooks';

export interface UseTeamAbsencesWidgetOutput {
  loading: boolean;
  failedToLoad: boolean;
  teamAbsences: People.AbsenceDto[];
  publicHolidays: People.PublicHolidayDto[];
  isTabDisabled: boolean;
  absencesRange: {
    startDate: Date;
    endDate: Date;
  };
}

export const useMyTeamAbsencesWidget = (
  config: MyTeamAbsencesWidgetProps['config'],
): UseTeamAbsencesWidgetOutput => {
  const dataRequestRoles = getWidgetDataRequestRoles(config?.role);

  const getTeamAbsencesReqParams = useMemo(
    () => ({
      employeeId: 'me',
      startDate: moment().startOf('day').utc(true).toDate(),
      endDate: moment()
        .add(1, 'month')
        .subtract(1, 'day')
        .endOf('day')
        .utc(true)
        .toDate(),
      roles: dataRequestRoles,
    }),
    [dataRequestRoles],
  );
  const {
    data: teamAbsences,
    loading: teamAbsencesLoading,
    failedToLoad: failedToLoadTeamAbsences,
  } = useGetTeamAbsences(getTeamAbsencesReqParams);

  const legalEntities = React.useMemo(() => {
    return teamAbsences?.reduce((acc: number[], curr: People.AbsenceDto) => {
      if (curr.employee.legalEntity) {
        return [...acc, curr.employee.legalEntity.id];
      }

      return acc;
    }, []);
  }, [teamAbsences]);

  const getPublicHolidaysReqParams = useMemo(
    () => ({
      legalEntities,
      countryCodes: undefined,
      years: uniq([
        moment().toDate().getFullYear(),
        moment().add(30, 'day').toDate().getFullYear(),
      ]),
    }),
    [legalEntities],
  );
  const {
    data: publicHolidays,
    loading: loadingPublicHolidays,
    failedToLoad: failedToLoadPublicHolidays,
  } = useGetPublicHolidays(getPublicHolidaysReqParams);

  const { isModuleTabEnabled } = useModulesConfig();

  return {
    loading: teamAbsencesLoading || loadingPublicHolidays,
    failedToLoad: failedToLoadTeamAbsences || failedToLoadPublicHolidays,
    teamAbsences: teamAbsences || [],
    publicHolidays: publicHolidays || [],
    isTabDisabled: !isModuleTabEnabled(TabModules.PEOPLE, PeopleTabs.Absences),
    absencesRange: {
      startDate: getTeamAbsencesReqParams.startDate,
      endDate: getTeamAbsencesReqParams.endDate,
    },
  };
};
